/**
 * MODULE: domain/pages/frontend/utils/databaseFieldUtils.ts
 * PURPOSE: Field-type helpers for database table cells (display, sort, search, schema)
 */

import type { FieldDefinition, FieldType } from '../../contracts/pages.types';

export type CellSaveValue = string | number | boolean | string[];

const ADDABLE_FIELD_TYPES: FieldType[] = [
  'text',
  'number',
  'select',
  'multi_select',
  'date',
  'checkbox',
  'url',
  'email',
  'phone',
];

export const DATABASE_ADDABLE_FIELD_TYPES = ADDABLE_FIELD_TYPES;

export function slugFieldId(name: string): string {
  const base =
    name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '') || 'field';
  return `${base}_${Math.random().toString(36).slice(2, 8)}`;
}

export function createFieldDefinition(
  name: string,
  fieldType: FieldType,
  optionsText?: string
): FieldDefinition {
  const field: FieldDefinition = {
    id: slugFieldId(name),
    name: name.trim(),
    field_type: fieldType,
  };

  if (fieldType === 'select' || fieldType === 'multi_select') {
    const labels = (optionsText ?? 'To do, In progress, Done')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    field.options = labels.map((label, index) => ({
      id: `opt_${index}_${label.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`,
      name: label,
    }));
  }

  return field;
}

export function normalizePropertyValue(field: FieldDefinition, raw: unknown): CellSaveValue {
  if (field.field_type === 'checkbox') {
    return raw === true || raw === 'true' || raw === 1 || raw === '1';
  }
  if (field.field_type === 'number') {
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    const n = Number(String(raw ?? '').replace(/,/g, ''));
    return Number.isFinite(n) ? n : '';
  }
  if (field.field_type === 'multi_select') {
    if (Array.isArray(raw)) return raw.map(String);
    if (typeof raw === 'string' && raw.trim()) {
      return raw.split(',').map((s) => s.trim()).filter(Boolean);
    }
    return [];
  }
  if (raw === null || raw === undefined) return '';
  return String(raw);
}

export function fieldToInlineType(
  field: FieldDefinition
): 'text' | 'number' | 'date' | 'select' | 'textarea' | null {
  if (field.field_type === 'checkbox' || field.field_type === 'multi_select') return null;
  if (field.field_type === 'number') return 'number';
  if (field.field_type === 'date') return 'date';
  if (field.field_type === 'select') return 'select';
  if (field.field_type === 'text' || field.field_type === 'url' || field.field_type === 'email') {
    return 'text';
  }
  if (field.field_type === 'phone') return 'text';
  return 'text';
}

export function getLinkView(field: FieldDefinition): 'none' | 'email' | 'tel' | 'url' {
  if (field.field_type === 'email') return 'email';
  if (field.field_type === 'phone') return 'tel';
  if (field.field_type === 'url') return 'url';
  return 'none';
}

export function validateFieldDraft(field: FieldDefinition, draft: string): string | null {
  if (field.field_type === 'email' && draft.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft)) {
    return 'Enter a valid email address.';
  }
  if (field.field_type === 'url' && draft.trim() && !/^https?:\/\/.+/i.test(draft)) {
    return 'Enter a valid URL starting with http:// or https://';
  }
  return null;
}

export function formatFieldDisplay(field: FieldDefinition, value: CellSaveValue): string {
  if (field.field_type === 'checkbox') {
    return value === true ? 'Yes' : 'No';
  }
  if (field.field_type === 'multi_select') {
    const ids = Array.isArray(value) ? value : [];
    if (!ids.length) return '—';
    const labels = ids.map((id) => field.options?.find((o) => o.id === id)?.name ?? id);
    return labels.join(', ');
  }
  if (field.field_type === 'select' && field.options?.length) {
    const opt = field.options.find((o) => o.id === String(value));
    if (opt) return opt.name;
  }
  if (value === '' || value === null || value === undefined) return '—';
  return String(value);
}

export function compareFieldValues(
  a: unknown,
  b: unknown,
  field: FieldDefinition
): number {
  const av = normalizePropertyValue(field, a);
  const bv = normalizePropertyValue(field, b);

  if (field.field_type === 'number') {
    const an = typeof av === 'number' ? av : Number(av);
    const bn = typeof bv === 'number' ? bv : Number(bv);
    return (Number.isFinite(an) ? an : 0) - (Number.isFinite(bn) ? bn : 0);
  }
  if (field.field_type === 'checkbox') {
    return Number(av === true) - Number(bv === true);
  }
  if (field.field_type === 'date') {
    return String(av).localeCompare(String(bv));
  }
  return String(av).localeCompare(String(bv), undefined, { numeric: true });
}

export function fieldMatchesSearch(
  field: FieldDefinition,
  value: unknown,
  query: string
): boolean {
  const normalized = normalizePropertyValue(field, value);
  const haystack =
    field.field_type === 'multi_select' && Array.isArray(normalized)
      ? normalized
          .map((id) => field.options?.find((o) => o.id === id)?.name ?? id)
          .join(' ')
      : formatFieldDisplay(field, normalized);
  return haystack.toLowerCase().includes(query);
}

export function isFieldSortable(field: FieldDefinition): boolean {
  return !['created_at', 'created_by'].includes(field.field_type);
}

export function findPrimaryTextField(fields: FieldDefinition[]): FieldDefinition | undefined {
  return fields.find((f) => f.id === 'name') ?? fields.find((f) => f.field_type === 'text');
}

export function canDeleteField(fields: FieldDefinition[], fieldId: string): boolean {
  const remaining = fields.filter((field) => field.id !== fieldId);
  if (!remaining.length) return false;
  return remaining.some((field) => field.field_type === 'text');
}

export function renameField(
  fields: FieldDefinition[],
  fieldId: string,
  newName: string
): FieldDefinition[] {
  const trimmed = newName.trim();
  if (!trimmed) return fields;
  return fields.map((field) =>
    field.id === fieldId ? { ...field, name: trimmed } : field
  );
}

export function deleteField(fields: FieldDefinition[], fieldId: string): FieldDefinition[] {
  if (!canDeleteField(fields, fieldId)) return fields;
  return fields.filter((field) => field.id !== fieldId);
}

export function reorderFields(
  fields: FieldDefinition[],
  fromIndex: number,
  toIndex: number
): FieldDefinition[] {
  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= fields.length ||
    toIndex >= fields.length
  ) {
    return fields;
  }
  const next = [...fields];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

export function moveFieldUp(fields: FieldDefinition[], fieldId: string): FieldDefinition[] {
  const index = fields.findIndex((field) => field.id === fieldId);
  if (index <= 0) return fields;
  return reorderFields(fields, index, index - 1);
}

export function moveFieldDown(fields: FieldDefinition[], fieldId: string): FieldDefinition[] {
  const index = fields.findIndex((field) => field.id === fieldId);
  if (index < 0 || index >= fields.length - 1) return fields;
  return reorderFields(fields, index, index + 1);
}
